// frontend/src/pages/EngagementRatePage.js
//
// Renders /tools/engagement-rate-calculator and its platform spokes from
// data/engagement-rate-pages.json.
//
// FORMAT CHOSEN FROM THE LIVE SERP, not from the keyword shape. Every organic
// result for `engagement rate calculator` is a working single-purpose tool
// (DashThis, Omni Calculator, GRIN, Social Insider, Nativz, 1stCollab) — no
// listicles at all. That is the opposite of `social media scheduling tools`,
// where the entire first page is vendor roundups and no product page can rank.
// So this is a tool page with the tool at the top, above the prose.
//
// 216 calculator-intent keywords, 48,200 searches, competition 5-17. Instagram
// carries four separate phrasings at the 5,000 bucket, so it gets the deepest
// spoke — the same shape as the best-time-to-post cluster.
//
// useLocation, NOT useParams: these are static routes with no :param, and
// useParams returns undefined here. That exact bug shipped once on the platform
// schedulers and rendered the 404 component under a correct prerendered title.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import EngagementRateCalculator from '../components/EngagementRateCalculator'
import pages from '../data/engagement-rate-pages.json'
import NotFound from './NotFound'

const BASE = '/tools/engagement-rate-calculator'

const HERO = {
  '':          ['/assets/images/articles/engagement-analytics.webp', 'Social media apps on a smartphone home screen'],
  instagram:   ['/assets/images/articles/engagement-instagram.webp', 'Someone checking their phone analytics at a cafe table'],
  tiktok:      ['/assets/images/articles/engagement-tiktok.webp',    'A creator filming vertical video on a phone gimbal'],
}

export default function EngagementRatePage () {
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+tools\/engagement-rate-calculator\/?/, '').replace(/\/+$/, '')
  const p = pages.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const url = `${BASE}${p.slug ? `/${p.slug}` : ''}`
  const [heroSrc, heroAlt] = HERO[p.slug] || HERO['']

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={url}
        image={heroSrc}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: p.faqs.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: p.h1,
            url: `${SITE_URL}${url}`,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-0'>{p.sub}</p>
            </Col>
            <Col lg={5}>
              <Image src={heroSrc} alt={heroAlt} fluid rounded loading='eager'
                     width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE TOOL — it is the search intent, so it goes first ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <EngagementRateCalculator platform={p.slug || 'instagram'} />
        </Container>
      </section>

      {/* ───────── THE ANSWER (snippet target) ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <p className='fs-5' dangerouslySetInnerHTML={{ __html: p.intro }} />
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── TABLE ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-4'>
            {p.slug ? `What ${p.platform} engagement rates actually look like` : 'How each platform defines engagement rate'}
          </h2>
          <div className='table-responsive mb-3'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr>{p.table_head.map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {p.table.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={j === 0 ? 'fw-semibold' : j === 1 ? 'text-primary fw-semibold' : 'text-muted'}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>{p.outro}</p>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {p.sections.map(([heading, body], i) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly Social
                you can write a post once, give it a different caption per network, and schedule it
                to all eight — Instagram, TikTok, Facebook, LinkedIn, YouTube, Pinterest, Threads
                and X — from a single calendar.
              </p>
              <p className='text-muted'>
                It exists because the two things that move the number you just calculated —
                posting consistently, and posting at the right time — are both scheduling
                problems. Plans start at $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — see standing rule 2. */}
              <Image src='/assets/logo/calender.webp' alt='The Greatly Social content calendar'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {pages.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug || 'pillar'}>
                <a href={`${BASE}${x.slug ? `/${x.slug}` : ''}`}
                   className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.platform} engagement rate</div>
                  <div className='text-muted small'>{x.slug ? 'Platform calculator' : 'Every platform compared'}</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/best-time-to-post' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Best time to post</div>
                <div className='text-muted small'>Build a schedule around your audience</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/tools/utm-builder' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>UTM builder</div>
                <div className='text-muted small'>Track which posts drive traffic</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>Measuring it is the easy part</h2>
          <p className='text-muted fs-5'>
            At Greatly Social you can schedule a week of posts in one sitting, so consistency
            stops depending on whether you remember.
          </p>
          <Row className='g-3 mb-4'>
            {[['Schedule once, publish weekly', 'Set your slots and let them run.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A month in one upload', 'Queue a whole month from a spreadsheet.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a list.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can post consistently without thinking about it</h2>
          <p className='lead mb-4'>
            Schedule a week across eight networks in one sitting. Free for 14 days, no card,
            plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/best-time-to-post' className='mb-2'>
            Find your best times
          </Button>
        </div>
      </section>
    </>
  )
}
